import React, { useState, useEffect, useRef } from 'react';
import OtpInput from '../Login/utils/OtpInput';
import CreatePinInput from '../CreatePin/CreatePinInput';
import { validateOtp } from '../Login/utils/validate';
import { userAPI } from '../../../services/api';

import { useTranslation } from "react-i18next";

const ChangePin = ({ onChangePin, onCancel, loading = false }) => {
  const { t } = useTranslation("auth");
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    otp: '',
    new_pin: '',
    confirm_pin: ''
  });
  const [errors, setErrors] = useState({});
  const [otpSending, setOtpSending] = useState(false);
  
  const otpSentRef = useRef(false);


  useEffect(() => {

    if (!otpSentRef.current) {
      sendInternalOTP();
    }
  }, []);

  const sendInternalOTP = async () => {

    if (otpSending || otpSentRef.current) {
      console.log('🚫 OTP уже отправляется или был отправлен');
      return;
    }
    
    console.log('🔄 Отправка OTP');
    setOtpSending(true);
    otpSentRef.current = true; 
    
    try {
      const success = await onChangePin?.({ type: 'request_internal_otp' });
      
      if (success !== false) {
        setStep(2); 
        setErrors({}); 

      } else {
        setErrors({ general: t("SMS_SEND_ERR") });
        otpSentRef.current = false; 
      }
    } catch (error) {

      setErrors({ general: t("SMS_SEND_ERR") });
      otpSentRef.current = false; 
    } finally {
      setOtpSending(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }

    if (field === 'new_pin' && value.length === 4 && step === 3) {
      setTimeout(() => setStep(4), 300);
    } else if (field === 'confirm_pin' && value.length === 4 && step === 4) {
      handlePinSubmit({ ...formData, [field]: value });
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();

    const otpErrors = validateOtp(formData.otp);
    if (Object.keys(otpErrors).length > 0) {
      setErrors(otpErrors);
      return;
    }

    try{
      const response = await userAPI.resetPincodeConfirm(formData.otp)
      
      const { is_reseted } = response.data

      if(is_reseted === true){
        setStep(3);
      }
    }
    catch(e){
      const errors = {}
      errors.otp = t("SMS_SEND_ERR")
      setErrors(errors);
    }
  };

  const handlePinSubmit = async (data) => {
    if (data.new_pin !== data.confirm_pin) {
      setErrors({ confirm_pin: t("PINS_DOESNT_MATCH_ERR") });
      setFormData(prev => ({ ...prev, confirm_pin: '' }));
      
      setTimeout(() => {
        setErrors({});
      }, 3000);
      return;
    }

    const dataToSend = {
      otp: formData.otp.replace(/\D/g, ''),
      new_pin: data.new_pin
    };

    if (onChangePin) {
      onChangePin({ type: 'confirm_change', data: dataToSend });
    }
  };

  const handleBackToOtp = () => {
    setStep(2);
    setFormData(prev => ({ ...prev, new_pin: '', confirm_pin: '' }));
    setErrors({});
  };

  const handleBackToNewPin = () => {
    setStep(3);
    setFormData(prev => ({ ...prev, confirm_pin: '' }));
    setErrors({});
  };

  const handleResendOTP = async () => {

    otpSentRef.current = false;
    setFormData(prev => ({ ...prev, otp: '' }));
    setErrors({});
    
    await sendInternalOTP();
  };

  const getStepContent = () => {
    switch (step) {
      case 1:
        return {
          title: t("SENDING_SMS"),
          description: t("WE_SENDING_SMS")
        };
      case 2:
        return {
          title: t("ENTER_SMS"),
          description: t("WE_SENDED_SMS")
        };
      case 3:
        return {
          title: t("NEW_PIN"),
          description: t("IMAGINE_NEW_PIN")
        };
      case 4:
        return {
          title: t("APROVE_NEW_PIN"),
          description: t("REPEAT_NEW_PIN")
        };
      default:
        return {};
    }
  };

  const stepContent = getStepContent();

  return (
    <div className="fixed inset-0 bg-white flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-8 w-full max-w-md mx-auto shadow-2xl animate-fadeIn">
        
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-3">
            {stepContent.title}
          </h1>
          <p className="text-gray-500 text-base">
            {stepContent.description}
          </p>
          {errors.general && (
            <p className="text-red-500 text-sm mt-2">{errors.general}</p>
          )}
        </div>

        <div className="flex flex-col gap-6">
          {step === 1 && (
            <div className="text-center py-8">
              <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-gray-600 font-medium">
                {otpSending ? t("IS_SENDING_SMS") : t("GETTING_READY")}...
              </p>
            </div>
          )}

          {step === 2 && (
            <>
              <OtpInput
                value={formData.otp}
                onChange={(value) => handleInputChange('otp', value)}
                error={errors.otp}
                disabled={loading}
              />
              
              <button
                onClick={handleOtpSubmit}
                disabled={loading}
                className="w-full bg-blue-500 text-white py-4 rounded-xl font-semibold text-lg transition 
                           hover:bg-blue-600 active:scale-95 disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="flex items-center justify-center gap-3">
                    <div className="w-5 h-5 border-2 border-transparent border-t-white rounded-full animate-spin"></div>
                    {t("CHECKING")}...
                  </div>
                ) : (
                  t("ACCEPT")
                )}
              </button>

              <button
                onClick={handleResendOTP}
                disabled={loading || otpSending}
                className="w-full text-blue-500 font-medium py-3 hover:text-blue-600 disabled:opacity-60"
              >
                {otpSending ? t("SENDING") + '...' : '↻' + t("RESEND")}
              </button>
            </>
          )}

          {step === 3 && (
            <>
              <CreatePinInput
                value={formData.new_pin}
                onChange={(value) => handleInputChange('new_pin', value)}
                error={errors.new_pin}
                disabled={loading}
                title=""
                description=""
                hideTitle={true}
              />
              
              <button
                onClick={handleBackToOtp}
                disabled={loading}
                className="w-full text-blue-500 font-medium py-3 hover:text-blue-600 disabled:opacity-60"
              >
                ← {t("CHANGE_ACCEPT_CODE")}
              </button>
            </>
          )}

          {step === 4 && (
            <>
              <CreatePinInput
                value={formData.confirm_pin}
                onChange={(value) => handleInputChange('confirm_pin', value)}
                error={errors.confirm_pin}
                disabled={loading}
                title=""
                description=""
                hideTitle={true}
              />
              
              <button
                onClick={handleBackToNewPin}
                disabled={loading}
                className="w-full text-blue-500 font-medium py-3 hover:text-blue-600 disabled:opacity-60"
              >
                ← {t("CHANGE_PIN_CODE")}
              </button>
            </>
          )}

          <button
            onClick={onCancel}
            disabled={loading}
            className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-3 px-4 rounded-xl disabled:opacity-60 transition-all duration-200"
          >
            Отменить
          </button>
        </div>

        <div className="bg-blue-50 rounded-lg p-4 mt-6">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <div className="text-sm font-medium text-blue-900 mb-1">
                {step <= 2 ? 'Безопасность' : 'Почти готово!'}
              </div>
              <div className="text-xs text-blue-700">
                {step <= 2 
                  ? t("ACCEPT_CODE_IS_SENDING")
                  : t("NEW_PIN_WILL_BE_SAVED")
                }. {/* <--- ТУТ ТОЧКА */}
              </div>
            </div>
          </div>
        </div>

        {loading && (
          <div className="absolute inset-0 bg-white bg-opacity-90 rounded-2xl flex items-center justify-center">
            <div className="flex flex-col items-center">
              <div className="w-8 h-8 border-3 border-blue-200 border-t-blue-600 rounded-full animate-spin mb-4"></div>
              <p className="text-gray-600 font-medium">
                {step === 2 
                  ? t("CHECKING_CODE")
                  : t("CHANGING_PIN")
                }... {/* <--- ТУТ ТРОЕТОЧИЕ */}
              </p>
            </div>
          </div>
        )}
      </div>
      
      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.4s ease-out;
        }
      `}</style>
    </div>
  );
};

export default ChangePin;